import { ReactNode, useEffect, useState } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Theme,
  createStyles,
  Box,
  ClickAwayListener,
  Slide,
  Paper,
  BoxProps,
} from "@material-ui/core";

type RenderProps = {
  visible: boolean;
};

export type MasterDetailProps = {
  open: boolean;
  setOpen: (to?: boolean) => void;
  renderDetail: (props: RenderProps) => JSX.Element;
  children?: ReactNode | ((props: RenderProps) => JSX.Element);
} & BoxProps;

export const MasterDetail = ({
  setOpen,
  open,
  renderDetail,
  children,
  className,
  ...props
}: MasterDetailProps) => {
  const classes = useStyles();
  const [visible, setVisible] = useState(open);

  useEffect(() => {
    if (open) {
      setVisible(true);
    }
  }, [open]);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        display="flex"
        height="100%"
        flex={1}
        position="relative"
        className={clsx(classes.root, className)}
        {...props}
      >
        <div className={open ? classes.leftOpen : classes.left}>
          {children instanceof Function ? children({ visible }) : children}
        </div>
        <Slide
          direction="left"
          in={open}
          mountOnEnter
          unmountOnExit
          onExited={() => setVisible(false)}
          timeout={300}
        >
          <div className={classes.flexed}>
            <Paper className={classes.paper}>{renderDetail({ visible })}</Paper>
          </div>
        </Slide>
      </Box>
    </ClickAwayListener>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      overflowX: "hidden",
      padding: theme.spacing(2),
    },
    flexed: {
      flex: 1,
      top: theme.spacing(2),
      bottom: theme.spacing(2),
      zIndex: theme.zIndex.modal,
      position: "absolute",
      right: 0,
      marginRight: theme.spacing(2),
      width: `calc(50% - ${theme.spacing(2)}px)`,
      maxHeight: "100%",
      overflow: "initial",
    },
    paper: {
      boxShadow: "0 1px 1px 0 rgba(32, 37, 43, 0.12), 0 0 1px 0 rgba(32, 37, 43, 0.2)",
      overflowX: "auto",
      maxHeight: "100%",
    },
    left: {
      width: "100%",
      height: "100%",
      maxHeight: "100%",
      transition: "width 300ms cubic-bezier(0, 0, 0.2, 1)",
    },
    leftOpen: {
      width: "50%",
      height: "100%",
      maxHeight: "100%",
      transition: "width 300ms cubic-bezier(0, 0, 0.2, 1)",
    },
  }),
);
